import React, { useEffect, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { XR, XROrigin, useXRControllerLocomotion, useXRInputSourceState } from "@react-three/xr";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import AcousticDiorama from "@/components/scene/AcousticDiorama";
import StairwellScene from "@/components/scene/StairwellScene";
import PrologueScene from "@/components/scene/PrologueScene";
import ArrivalScene from "@/components/scene/ArrivalScene";
import SkyLobbyScene from "@/components/scene/SkyLobbyScene";
import VRComfort from "@/components/scene/VRComfort";
import ErrorBoundary from "@/components/ErrorBoundary";
import { S_POS, B_POS } from "@/hooks/useGame";

// forward unit-vector from yaw/pitch (yaw=0,pitch=0 → straight -Z into the scene)
function lookDir(yaw, pitch) {
  const cp = Math.cos(pitch);
  return [Math.sin(yaw) * cp, Math.sin(pitch), -Math.cos(yaw) * cp];
}

// Drag-to-look (desktop pointer) + touch-swipe (mobile). Updates game.lookYaw/Pitch.
// Inert during the 3rd-person orbit view and inside an active XR session.
function LookControls({ game, enabled }) {
  const gl = useThree((s) => s.gl);
  useEffect(() => {
    if (!enabled) return;
    const el = gl.domElement;
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    const onDown = (e) => {
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
    };
    const onMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      const g = game.current;
      g.lookYaw = (g.lookYaw || 0) - dx * 0.005;
      g.lookPitch = Math.max(-0.85, Math.min(0.85, (g.lookPitch || 0) - dy * 0.005));
    };
    const onUp = () => { dragging = false; };
    el.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [gl, enabled, game]);
  return null;
}

// Recompute vertical FOV from the live aspect ratio so portrait phones/tablets
// and spatial viewers keep a natural ~72° horizontal field of view instead of a
// cropped, zoomed-in frame. Inert inside XR (the headset owns projection).
function ResponsiveFov({ xrStore }) {
  const camera = useThree((s) => s.camera);
  const width = useThree((s) => s.size.width);
  const height = useThree((s) => s.size.height);
  useEffect(() => {
    if (xrStore && xrStore.getState && xrStore.getState().session) return;
    const aspect = Math.max(0.0001, width / height);
    const targetHFov = THREE.MathUtils.degToRad(72);
    let vFov = 2 * Math.atan(Math.tan(targetHFov / 2) / aspect);
    vFov = THREE.MathUtils.clamp(THREE.MathUtils.radToDeg(vFov), 46, 95);
    camera.fov = vFov;
    camera.updateProjectionMatrix();
  }, [camera, width, height, xrStore]);
  return null;
}

// Writes the live view heading (radians) into game.current.viewHeading each frame
// so the HUD compass can read it. First-person → look yaw; universal → orbit azimuth.
function HeadingWriter({ game, orbitOn }) {
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls);
  useFrame(() => {
    const g = game.current;
    if (!g) return;
    if (orbitOn) {
      // camera looks toward the LIVE orbit target (which pans with the D-pad), so the
      // compass stays correct after panning instead of assuming the target is the origin
      const tx = controls && controls.target ? controls.target.x : 0;
      const tz = controls && controls.target ? controls.target.z : 0;
      g.viewHeading = Math.atan2(tx - camera.position.x, tz - camera.position.z);
    } else {
      g.viewHeading = g.lookYaw || 0;
    }
  });
  return null;
}

function CameraRig({ game, xrStore, mode, viewMode, pov }) {
  const camera = useThree((s) => s.camera);
  useFrame((state) => {
    const g = game.current;
    if (xrStore && xrStore.getState && xrStore.getState().session) return;
    if (mode === "acoustic" && viewMode === "third") return; // orbit owns camera
    if (mode === "stairwell") return; // scene owns camera

    const t = state.clock.elapsedTime;
    const [dx, dy, dz] = lookDir(g.lookYaw || 0, g.lookPitch || 0);
    const aim = (px, py, pz) => camera.lookAt(px + dx * 4, py + dy * 4, pz + dz * 4);

    if (mode === "prologue") {
      const shake = g.impactT * (pov === "brian" ? 0.6 : 0.32);
      const sx = (Math.random() - 0.5) * shake;
      const sy = (Math.random() - 0.5) * shake;
      const ox = g.camOffX || 0, oz = g.camOffZ || 0;
      if (pov === "brian") camera.position.set(0 + sx + ox, 1.65 + sy, 4.2 + oz);
      else camera.position.set(2.2 + sx + ox, 1.9 + sy, 4.6 + oz);
      aim(camera.position.x, camera.position.y, camera.position.z);
      return;
    }

    if (mode === "acoustic" && viewMode === "first") {
      const bob = Math.sin(t * 2) * 0.02;
      const ox = g.camOffX || 0, oz = g.camOffZ || 0;
      if (pov === "brian") camera.position.set(B_POS.x - 0.2 + ox, 1.6 + bob, 0.5 + oz);
      else camera.position.set(S_POS.x + 0.2 + ox, 1.5 + bob, 0.5 + oz);
      aim(camera.position.x, camera.position.y, camera.position.z);
      return;
    }

    if (mode === "arrival") {
      // camera watches from the near curb, looking WEST down the street (-Z);
      // rumbling ground-shake intensifies as the tower comes down behind (roar → 1)
      const roar = g.roar || 0;
      const sh = roar * roar * 0.18;
      const ox = g.camOffX || 0, oz = g.camOffZ || 0;
      camera.position.set((Math.random() - 0.5) * sh + ox, 2.3 + (Math.random() - 0.5) * sh, 9 + (Math.random() - 0.5) * sh + oz);
      aim(0, 2.0, 9);
      return;
    }
  });
  return null;
}

function OrbitCam({ enabled, autoRotate, game, xrStore }) {
  const camera = useThree((s) => s.camera);
  const controlsRef = useRef();
  const prevOff = useRef({ x: 0, z: 0 });
  useEffect(() => {
    if (enabled) { camera.position.set(4.5, 2.8, 6.5); prevOff.current = { x: 0, z: 0 }; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
  useFrame(() => {
    const g = game && game.current;
    const ctrl = controlsRef.current;
    const inXR = !!(xrStore && xrStore.getState && xrStore.getState().session);
    if (!enabled || inXR || !ctrl || !g) { if (g) g.orbitOn = false; return; }
    g.orbitOn = true;
    // Pan by translating BOTH the orbit target and the camera by the SAME per-frame
    // delta of the D-pad offset, so drag-to-orbit, damping and auto-rotate all still
    // compose smoothly around the shifted pivot (no snap-back).
    const ox = g.camOffX || 0, oz = g.camOffZ || 0;
    const dx = ox - prevOff.current.x;
    const dz = oz - prevOff.current.z;
    if (dx !== 0 || dz !== 0) {
      ctrl.target.x += dx; ctrl.target.z += dz;
      camera.position.x += dx; camera.position.z += dz;
      prevOff.current.x = ox; prevOff.current.z = oz;
    }
    g.orbitYaw = Math.atan2(ctrl.target.x - camera.position.x, -(ctrl.target.z - camera.position.z));
  });
  if (!enabled) return null;
  return (
    <OrbitControls
      ref={controlsRef}
      makeDefault
      target={[0, 1.4, 0]}
      enablePan={false}
      minDistance={3}
      maxDistance={14}
      minPolarAngle={0.15}
      maxPolarAngle={Math.PI / 1.9}
      autoRotate={autoRotate}
      autoRotateSpeed={0.9}
      dampingFactor={0.08}
    />
  );
}

// Universal VR camera rig: thumbstick locomotion (controllers) + hand-tracking
// pinch-to-move-forward, translating the XR origin. Inert outside an XR session.
function VRRig({ game }) {
  const originRef = useRef();
  const leftController = useXRInputSourceState("controller", "left");
  const rightController = useXRInputSourceState("controller", "right");
  const rightHand = useXRInputSourceState("hand", "right");
  const leftHand = useXRInputSourceState("hand", "left");
  const fwd = useRef(new THREE.Vector3());

  const pinching = (h) => {
    const g = h && h.inputSource && h.inputSource.gamepad;
    return !!(g && g.buttons && g.buttons[0] && g.buttons[0].pressed);
  };

  // Locomotion via the callback form so we can SUPPRESS stick translation during the
  // descent phase (the stairwell scene moves the world) while KEEPING turning working.
  useXRControllerLocomotion((velocity, rotationVelocityY, delta) => {
    const o = originRef.current;
    if (!o) return;
    o.rotation.y += rotationVelocityY || 0; // right-stick turning always works
    const descending = !!(game && game.current && game.current.phase === "descent");
    if (!descending) {
      o.position.x += velocity.x * delta;
      o.position.z += velocity.z * delta;
    }
  });

  useFrame((state, dt) => {
    try {
      const g = game && game.current;
      const descending = !!(g && g.phase === "descent");
      const pinchHeld = pinching(rightHand) || pinching(leftHand);
      if (descending) {
        // VR descent trigger: left thumbstick forward, either trigger, or a held pinch
        const lt = leftController && leftController.gamepad;
        const rt = rightController && rightController.gamepad;
        const stickFwd = !!(lt && lt["xr-standard-thumbstick"] && (lt["xr-standard-thumbstick"].yAxis ?? 0) < -0.5);
        const triggerPressed =
          !!(lt && lt["xr-standard-trigger"] && lt["xr-standard-trigger"].state === "pressed") ||
          !!(rt && rt["xr-standard-trigger"] && rt["xr-standard-trigger"].state === "pressed");
        g.xrDescend = stickFwd || triggerPressed || pinchHeld;
        // do NOT move the origin here — the world descends instead
      } else {
        if (g) g.xrDescend = false;
        // pinch-to-glide stays available outside the descent phase (unchanged)
        if (originRef.current && pinchHeld) {
          const dir = fwd.current;
          state.camera.getWorldDirection(dir);
          dir.y = 0;
          if (dir.lengthSq() > 0) dir.normalize();
          originRef.current.position.addScaledVector(dir, dt * 1.6);
        }
      }
    } catch (e) {}
  });
  // Single XR origin for the app (spawn matches the old VRComfort origin).
  return <XROrigin ref={originRef} position={[0, 0, 3.2]} />;
}

export default function SceneCanvas({ game, audio, mode, viewMode, pov, xrStore, gate, tour }) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const orbitOn = mode === "acoustic" && (viewMode === "third" || tour);
  const skyLobby = gate === "sky44";
  return (
    <Canvas
      dpr={dpr}
      gl={{ antialias: true, powerPreference: "high-performance", logarithmicDepthBuffer: true }}
      camera={{ fov: 46, near: 0.1, far: 120, position: [0.4, 2.5, 8.6] }}
      style={{ position: "absolute", inset: 0, touchAction: "none" }}
    >
      <color attach="background" args={[0x0c0d0f]} />
      <XR store={xrStore}>
        <VRRig game={game} />
        <LookControls game={game} enabled={!orbitOn} />
        <ResponsiveFov xrStore={xrStore} />
        <HeadingWriter game={game} orbitOn={orbitOn} />
        <CameraRig game={game} xrStore={xrStore} mode={mode} viewMode={viewMode} pov={pov} />
        <OrbitCam enabled={orbitOn} autoRotate={!!tour} game={game} xrStore={xrStore} />
        {skyLobby ? (
          <SkyLobbyScene game={game} />
        ) : (
          <>
            {mode === "prologue" && <PrologueScene game={game} pov={pov} />}
            {mode === "acoustic" && <AcousticDiorama game={game} audio={audio} />}
            {mode === "stairwell" && <StairwellScene game={game} audio={audio} />}
            {mode === "arrival" && <ArrivalScene game={game} audio={audio} />}
          </>
        )}
        <ErrorBoundary fallback={null}>
          <VRComfort game={game} />
        </ErrorBoundary>
      </XR>
    </Canvas>
  );
}
